import React, { useMemo, useState } from "react";
import { Card, Timeline, Tag, Typography, Space, Modal, Empty } from "antd";
import { useSelector } from "react-redux";
import DataBadge from "../ui/DataBadge";

const { Text } = Typography;

// Campos técnicos que no le sirven al usuario en el detalle del movimiento.
const CAMPOS_OCULTOS = new Set([
  "ip_address",
  "user_agent",
  "id_conciliacion",
  "id_conciliacion_audiencia",
  "id_user",
]);

const labelizeCampo = (key) =>
  key
    .replace(/^id_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const formatValor = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// El backend guarda el payload capturado tal cual bajo "create"/"update";
// aquí solo se aplana para mostrarlo como lista de campo: valor.
const getCamposEditados = (changesObj) => {
  if (!changesObj || typeof changesObj !== "object") return [];
  const inner = changesObj.update || changesObj.create || changesObj.delete || changesObj;
  if (!inner || typeof inner !== "object") return [];
  return Object.entries(inner)
    .filter(([k, v]) => !CAMPOS_OCULTOS.has(k) && v !== null && v !== undefined && v !== "")
    .map(([k, v]) => ({ campo: labelizeCampo(k), valor: formatValor(v) }));
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const safeJsonParse = (v) => {
  if (!v) return null;
  if (typeof v === "object") return v;
  if (typeof v !== "string") return null;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const getActionMeta = (actionRaw) => {
  const a = (actionRaw || "").toUpperCase();
  if (a === "CREATE") return { color: "#16A34A", label: "Registro", icon: "fa-circle-plus" };
  if (a === "UPDATE") return { color: "#0A539A", label: "Actualización", icon: "fa-pen" };
  if (a === "DELETE") return { color: "#DC2626", label: "Eliminación", icon: "fa-trash" };
  return { color: "#6B7280", label: "Actividad", icon: "fa-circle-info" };
};

const pickCodeFromChanges = (changesObj) => {
  if (!changesObj || typeof changesObj !== "object") return null;
  if (changesObj.expediente_format) return changesObj.expediente_format;
  if (changesObj.expediente) return changesObj.expediente;
  if (changesObj.code) return changesObj.code;
  return null;
};

const normalizeAuditItem = (raw, idx) => {
  const changesObj = safeJsonParse(raw.changes);
  const extraObj = safeJsonParse(raw.extra);

  const userName = (raw.user_name || "").trim();
  const userEmail = (raw.user_email || "").trim();

  return {
    id: raw.id_audit_log ?? idx,
    when: raw.datetime || null,
    action: raw.action || "",
    message: raw.message || "",
    idKey: raw.id_key ?? null,
    idModulo: raw.id_modulo ?? null,
    userName: userName || "—",
    userEmail: userEmail || "",
    code: pickCodeFromChanges(changesObj),
    endpoint: extraObj?.endpoint || "",
    origen: extraObj?.origen || "",
    changesObj,
    extraObj,
  };
};

export default function ActividadExpedienteCard({ items: itemsProp } = {}) {
  const auditRaw = useSelector((state) => state.conciliacion?.audit);
  const auditItems = itemsProp || auditRaw?.items || [];
  const [selected, setSelected] = useState(null);

  const items = useMemo(() => {
    return auditItems.map((it, idx) => normalizeAuditItem(it, idx));
  }, [auditItems]);

  return (
    <section className="movimientos-wrap">
      <Card
        className="movimientos-card"
        title={
          <Space size={10} align="center">
            <span>Actividad del expediente</span>
            <Tag className="movimientos-count">{items.length}</Tag>
          </Space>
        }
        bodyStyle={{ padding: 18 }}
      >
        {items.length === 0 ? (
          <Text type="secondary">No hay actividad registrada.</Text>
        ) : (
          <Timeline className="movimientos-timeline">
            {items.map((m) => {
              const meta = getActionMeta(m.action);

              return (
                <Timeline.Item key={m.id} color={meta.color}>
                  <div
                    className="mov-item mov-item-clickable"
                    onClick={() => setSelected(m)}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="mov-accent" style={{ background: meta.color }} />

                    <div className="mov-main">
                      <DataBadge color={meta.color} className="mov-tag">
                        {meta.label}
                      </DataBadge>

                      <Text strong className="mov-message">
                        {m.message || "—"}
                      </Text>
                    </div>

                    <div className="mov-meta">
                      <Text className="mov-date">
                        {fmtDateTime(m.when)}
                      </Text>

                      <div className="mov-userbox">
                        <Text className="mov-user-name">
                          {m.userName}
                        </Text>
                        {m.userEmail ? (
                          <Text className="mov-user-email">
                            {m.userEmail}
                          </Text>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </Timeline.Item>
              );
            })}
          </Timeline>
        )}
      </Card>

      <Modal
        open={!!selected}
        onCancel={() => setSelected(null)}
        footer={null}
        title={selected ? getActionMeta(selected.action).label : ""}
      >
        {selected && (
          <div className="mov-detail">
            <Text strong className="mov-detail-message">{selected.message || "—"}</Text>

            <div className="mov-detail-meta">
              <Text type="secondary">{fmtDateTime(selected.when)}</Text>
              <Text type="secondary">
                {selected.userName}
                {selected.userEmail ? ` · ${selected.userEmail}` : ""}
              </Text>
            </div>

            {(() => {
              const campos = getCamposEditados(selected.changesObj);
              if (campos.length === 0) {
                return <Empty description="Sin detalle capturado para este movimiento" />;
              }
              return (
                <div className="mov-detail-campos">
                  {campos.map(({ campo, valor }) => (
                    <div className="mov-detail-campo" key={campo}>
                      <span className="mov-detail-campo-label">{campo}</span>
                      <span className="mov-detail-campo-valor">{valor}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </Modal>

      <style>{`
        .movimientos-wrap {
          width: 100%;
          padding: 0;
        }

        .movimientos-card {
          border-radius: 14px;
          overflow: hidden;
        }

        .movimientos-count {
          border-radius: 999px;
          padding: 0 10px;
          margin-left: 6px;
          background: #DCE9F5;
          color: #0A539A;
          border-color: #DCE9F5;
        }

        .movimientos-timeline {
          margin-top: 6px;
        }

        .mov-item {
          display: flex;
          align-items: center;
          gap: 16px;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.06);
          border-radius: 14px;
          padding: 14px 18px;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.04);
          transition: transform 0.12s ease, box-shadow 0.12s ease;
        }

        .mov-item-clickable {
          cursor: pointer;
        }

        .mov-item:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 26px rgba(0, 0, 0, 0.07);
        }

        .mov-detail-message {
          display: block;
          font-size: 14px;
          margin-bottom: 4px;
        }

        .mov-detail-meta {
          display: flex;
          flex-direction: column;
          gap: 2px;
          margin-bottom: 16px;
        }

        .mov-detail-campos {
          border-top: 1px solid rgba(0, 0, 0, 0.06);
          padding-top: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mov-detail-campo {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          font-size: 13px;
        }

        .mov-detail-campo-label {
          color: #6b7280;
          font-weight: 600;
          flex-shrink: 0;
        }

        .mov-detail-campo-valor {
          color: #111827;
          text-align: right;
          word-break: break-word;
        }

        .mov-accent {
          align-self: stretch;
          width: 4px;
          border-radius: 999px;
          flex-shrink: 0;
        }

        .mov-main {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .mov-tag {
          flex-shrink: 0;
          margin: 0;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .mov-message {
          display: block;
          font-size: 13.5px;
          line-height: 1.35;
          word-break: break-word;
        }

        .mov-meta {
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }

        .mov-date {
          font-size: 12.5px;
          font-weight: 700;
          color: #374151;
          white-space: nowrap;
        }

        .mov-userbox {
          text-align: right;
          line-height: 1.3;
        }

        .mov-user-name {
          font-size: 13px;
          font-weight: 700;
          color: #111827;
          display: block;
          white-space: nowrap;
        }

        .mov-user-email {
          font-size: 12px;
          font-weight: 500;
          color: #6b7280;
          display: block;
          white-space: nowrap;
        }

        @media (max-width: 768px) {
          .mov-item {
            flex-wrap: wrap;
          }
          .mov-main {
            flex-wrap: wrap;
          }
          .mov-meta {
            align-items: flex-start;
            width: 100%;
            padding-left: 18px;
          }
          .mov-userbox {
            text-align: left;
          }
          .mov-user-name,
          .mov-user-email {
            white-space: normal;
          }
        }
      `}</style>
    </section>
  );
}
